import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { sendWithFile } from "../../../common/api/apiShared";
import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { isNullOrWhitespace } from "@common/stringOperations";
import { useTranslation } from "../../../localization/useTranslation";
import { IAgreementDialog } from "./DataProcessorAgreementDialog";
import { DataProcessorAgreementSaveModel } from "./DataProcessorAgreementDialog.types";
import { useValidator } from "../../../common/hooks/validation";
import { useDocumentHook } from "../../../documents/Documents.hook";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useSelectableDocumentTypesQueryByUsageType } from "../../../common/hooks/useSelectableItemQueries";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useAgreementDialogHooks(props: IAgreementDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const [viewModel, setViewModel] = useState<DataProcessorAgreementSaveModel | undefined>(props.saveModel);
    const [documentationOption, setDocumentationOption] = useState(1);
    const [file, setFile] = useState<File>();
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();
    const { data: documentTypes, isLoading: documentTypesIsLoading } = useSelectableDocumentTypesQueryByUsageType(props.usageType);
    const { gdpo } = useUserContext();

    const validation = useValidator<DataProcessorAgreementSaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            documentTypeId: validators.validateNotEmpty((item) => item.documentTypeId, translateString("type")),
            fileName: documentationOption === 0 ? undefined : validators.validateNotEmpty((item) => item.fileName, translateString("file")),
            link: documentationOption === 1 ? undefined : validators.validateUrlAndNotEmpty((item) => item.link, translateString("link")),
            status: validators.validateNotEmpty((item) => item.status, translateString("status")),
            groupEntityAccessIds: validators.validateNotEmpty(
                (item) => item.groupEntityAccessIds,
                "",
                () => gdpo || props.saveModel.isGroupEntity!,
                translateString("oneGroupCompanyIsRequired")
            ),
        }),
        [documentationOption]
    );

    let documentStatus = useDocumentHook().selecableDocumentStatus;

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: any) => {
            if (response.success()) {
                const value = response.value();
                props.onSave(value.id);
                props.onDialogClose();
                setViewModel(undefined);
                snackbar.show(createElement(Trans, { i18nKey: "dataprocessorAgreementCreated", values: { name: viewModel?.name } }));
            } else {
                snackbar.show(translateString("dataprocessorAgreementExists"), "warning");
            }
        },
    });

    const createAgreement = async () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        await saveMutation.mutateAsync();
    };

    function save() {
        return sendWithFile<string>("POST", "/agreement/", viewModel!, file, trackUploadProgress());
    }

    function onDocumentationTypeChange(option: number) {
        let tempModel = { ...viewModel! };
        if (option === 0) {
            setFile(undefined);
            tempModel.fileName = "";
            setViewModel(tempModel);
            setDocumentationOption(0);
        }

        if (option === 1) {
            tempModel.link = "";
            setViewModel(tempModel);
            setDocumentationOption(1);
        }
    }

    function onFilesChanged(files: Array<DotLegalFile> | undefined) {
        let tempDoc = { ...viewModel! };
        setFile(files?.[0].file);
        tempDoc.fileName = files ? files[0].fileName : "";

        if (isNullOrWhitespace(tempDoc.name)) {
            tempDoc.name = tempDoc.fileName.substring(0, tempDoc.fileName.lastIndexOf("."));
        }

        setViewModel(tempDoc);
    }

    function onGroupEntityAccessIdsChange(groupEntityIds: Array<string>) {
        let tempModel = { ...viewModel! };
        tempModel.groupEntityAccessIds = groupEntityIds;

        if (groupEntityIds.length > 0) {
            tempModel.allGroupEntities = false;
        }

        setViewModel(tempModel);
    }

    function onAllGroupEntitiesChange(allGroupEntities: boolean) {
        let tempModel = { ...viewModel! };
        tempModel.allGroupEntities = allGroupEntities;

        if (allGroupEntities) {
            tempModel.groupEntityAccessIds = [];
        }

        setViewModel(tempModel);
    }

    return {
        inProgress: saveMutation.isLoading,
        uploadPercentage,
        onFilesChanged,
        viewModel,
        documentTypes,
        setViewModel,
        documentationOption,
        onDocumentationTypeChange,
        validation,
        createAgreement,
        documentStatus,
        onGroupEntityAccessIdsChange,
        onAllGroupEntitiesChange,
        gdpo,
        documentTypesIsLoading,
    };
}
